/*
 * FICHIER: GlobalStyles.js
 * BUT: Design System et styles globaux de l'application
 * 
 * Ce fichier définit le système de design complet de l'application :
 * - Variables CSS custom properties pour consistance
 * - Palette de couleurs avec variantes (primary, secondary, accent)
 * - Système d'espacement harmonieux
 * - Typographie avec hiérarchie claire
 * - Styles de base pour tous les éléments HTML
 * - Composants de titre réutilisables
 * - Responsive design avec breakpoints
 * 
 * AVANTAGES DU DESIGN SYSTEM :
 * - Consistance visuelle dans toute l'app
 * - Maintenance centralisée des styles
 * - Évolutivité et modifications faciles
 * - Performance avec CSS custom properties
 * - Accessibilité intégrée
 * 
 * PATTERN :
 * - CSS custom properties (variables CSS natives)
 * - Global styles avec createGlobalStyle
 * - Composants stylés exportés pour réutilisation
 * - Mobile-first responsive design
 */

import { createGlobalStyle } from 'styled-components';    // Global styles de styled-components
import styled from 'styled-components';                  // CSS-in-JS pour composants

// COMPOSANTS DE TITRE RÉUTILISABLES

/**
 * TITRE DE PAGE: Titre principal H1 avec style uniforme
 * 
 * Utilisé pour les titres principaux de chaque page.
 * Inclut un soulignement décoratif centré.
 */
export const PageTitle = styled.h1`
  font-size: var(--page-title-size);           // Taille définie dans les variables
  font-weight: var(--page-title-weight);       // Poids défini dans les variables
  color: var(--page-title-color);              // Couleur définie dans les variables
  text-align: center;                          // Centrage horizontal
  margin-top: calc(var(--page-title-margin-top) + 2rem);  // Marge dynamique avec offset
  margin-bottom: var(--space-xl);              // Espacement standardisé
  position: relative;                          // Pour le pseudo-élément de soulignement
  
  // SOULIGNEMENT DÉCORATIF
  &::after {
    content: '';                               // Pseudo-élément vide
    position: absolute;
    bottom: -10px;                             // En dessous du texte
    left: 50%;
    transform: translateX(-50%);               // Centrage horizontal parfait
    width: var(--page-title-underline-width); // Largeur définie dans les variables
    height: var(--page-title-underline-height); // Hauteur définie dans les variables
    background-color: var(--page-title-underline-color); // Couleur définie dans les variables
    border-radius: 2px;                       // Coins légèrement arrondis
  }
`;

/**
 * TITRE DE SECTION: Titre H2 pour les sections
 * 
 * Utilisé pour subdiviser le contenu en sections distinctes.
 * Style similaire au PageTitle mais plus petit.
 */
export const SectionTitle = styled.h2`
  font-size: var(--section-title-size);
  font-weight: var(--section-title-weight);
  color: var(--page-title-color);              // Même couleur que les titres de page
  text-align: center;
  margin-top: var(--space-xxl);               // Grande marge supérieure pour séparation
  margin-bottom: var(--space-xl);
  position: relative;
  
  // SOULIGNEMENT DÉCORATIF (plus petit que PageTitle)
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: var(--section-title-underline-width);   // Plus petit que PageTitle
    height: var(--section-title-underline-height); // Plus fin que PageTitle
    background-color: var(--page-title-underline-color);
    border-radius: 2px;
  }
`;

/**
 * SOUS-TITRE: Titre H3 pour les sous-sections
 * 
 * Utilisé pour des subdivisions plus fines sans soulignement.
 */
export const Subtitle = styled.h3`
  font-size: var(--subtitle-size);
  font-weight: var(--subtitle-weight);
  color: var(--gray-dark);                     // Couleur plus discrète
  text-align: center;
  margin-top: var(--space-xl);
  margin-bottom: var(--space-lg);
`;

/**
 * STYLES GLOBAUX: Configuration CSS pour toute l'application
 * 
 * Définit les variables CSS et les styles de base pour tous les éléments.
 * Utilise createGlobalStyle pour injecter du CSS global.
 */
const GlobalStyles = createGlobalStyle`
  :root {
    /* ===============================================
     * DESIGN SYSTEM - VARIABLES CSS CUSTOM PROPERTIES
     * =============================================== */
    
    /* PALETTE DE COULEURS PRINCIPALES */
    --primary: #1a73e8;              /* Bleu principal (boutons, liens) */
    --primary-dark: #0d47a1;         /* Bleu foncé (hover, active) */
    --primary-light: #4285f4;        /* Bleu clair (backgrounds, accents) */
    
    /* PALETTE DE COULEURS SECONDAIRES */
    --secondary: #34a853;            /* Vert (succès, validation) */
    --secondary-dark: #1e8e3e;       /* Vert foncé (hover) */
    --secondary-light: #66bb6a;      /* Vert clair (backgrounds) */
    
    /* PALETTE D'ACCENTUATION */
    --accent: #ea4335;               /* Rouge (erreurs, suppression) */
    --accent-light: #ff5252;         /* Rouge clair (hover) */
    --accent-dark: #b71c1c;          /* Rouge foncé (active) */
    
    /* COULEURS NEUTRES (GRAYS) */
    --dark: #202124;                 /* Noir principal (titres) */
    --gray-dark: #3c4043;            /* Gris foncé (texte principal) */
    --gray: #5f6368;                 /* Gris moyen (texte secondaire) */
    --gray-light: #9aa0a6;           /* Gris clair (texte discret) */
    --gray-lighter: #dadce0;         /* Gris très clair (bordures) */
    --light: #f8f9fa;                /* Gris très clair (backgrounds) */
    --white: #ffffff;                /* Blanc pur */
    
    /* SYSTÈME D'ESPACEMENT HARMONIEUX */
    --space-xs: 0.25rem;             /* 4px - Très petit espacement */
    --space-sm: 0.5rem;              /* 8px - Petit espacement */
    --space-md: 1rem;                /* 16px - Espacement moyen (base) */
    --space-lg: 1.5rem;              /* 24px - Grand espacement */
    --space-xl: 2rem;                /* 32px - Très grand espacement */
    --space-xxl: 3rem;               /* 48px - Espacement maximum */
    
    /* TYPOGRAPHIE */
    --font-primary: 'Google Sans', 'Segoe UI', Roboto, Arial, sans-serif;   /* Police principale */
    --font-secondary: 'Roboto', Arial, sans-serif;                          /* Police secondaire */
    
    /* ÉCHELLE TYPOGRAPHIQUE */
    --font-size-xs: 0.75rem;         /* 12px - Très petit texte */
    --font-size-sm: 0.875rem;        /* 14px - Petit texte */
    --font-size-md: 1rem;            /* 16px - Texte de base */
    --font-size-lg: 1.25rem;         /* 20px - Grand texte */
    --font-size-xl: 1.5rem;          /* 24px - Très grand texte */
    --font-size-xxl: 2rem;           /* 32px - Titre section */
    --font-size-xxxl: 3rem;          /* 48px - Titre principal */
    
    /* SYSTÈME DE BORDURES ARRONDIES */
    --border-radius-sm: 4px;         /* Coins légèrement arrondis */
    --border-radius-md: 8px;         /* Coins moyennement arrondis */
    --border-radius-lg: 12px;        /* Coins bien arrondis */
    --border-radius-xl: 20px;        /* Coins très arrondis */
    --border-radius-xxl: 28px;       /* Coins maximum */
    
    /* SYSTÈME D'OMBRES (DEPTH) */
    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);         /* Ombre légère */
    --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.1);          /* Ombre moyenne */
    --shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.1);        /* Ombre importante */
    --shadow-xl: 0 20px 25px rgba(0, 0, 0, 0.15);       /* Ombre maximum */
    
    /* TRANSITIONS ET ANIMATIONS */
    --transition-fast: 0.15s ease;                       /* Animation rapide */
    --transition-normal: 0.25s ease;                     /* Animation normale */
    --transition-slow: 0.5s ease;                        /* Animation lente */
    
    /* OPACITÉS STANDARDISÉES */
    --opacity-light: 0.1;            /* Très transparent */
    --opacity-medium: 0.5;           /* Semi-transparent */
    --opacity-high: 0.8;             /* Peu transparent */
    
    /* LARGEURS DE CONTENEUR RESPONSIVE */
    --container-sm: 640px;           /* Mobile large */
    --container-md: 768px;           /* Tablette */
    --container-lg: 1024px;          /* Desktop */
    --container-xl: 1280px;          /* Large desktop */
    
    /* SYSTÈME Z-INDEX (LAYERING) */
    --z-background: -10;             /* Arrière-plan */
    --z-normal: 1;                   /* Contenu normal */
    --z-overlay: 10;                 /* Overlays légers */
    --z-dropdown: 20;                /* Dropdowns */
    --z-sticky: 30;                  /* Éléments sticky */
    --z-fixed: 40;                   /* Éléments fixes (navbar) */
    --z-modal: 50;                   /* Modales */
    --z-popover: 60;                 /* Popovers */
    --z-tooltip: 70;                 /* Tooltips */
    
    /* VARIABLES SPÉCIFIQUES AUX TITRES */
    --page-title-color: #132e57;                         /* Bleu foncé pour titres */
    --page-title-size: 2.5rem;                          /* 40px */
    --page-title-weight: 700;                           /* Gras */
    --page-title-underline-color: #4a6bce;              /* Bleu pour soulignement */
    --page-title-underline-width: 80px;                 /* Largeur soulignement */
    --page-title-underline-height: 4px;                 /* Hauteur soulignement */
    --page-title-margin-top: 100px;                     /* Marge top (compense navbar) */
    
    /* VARIABLES DE SECTION */
    --section-title-size: 2rem;                         /* 32px */
    --section-title-weight: 600;                        /* Semi-gras */
    --section-title-underline-width: 60px;              /* Plus petit que page title */
    --section-title-underline-height: 3px;              /* Plus fin que page title */
    
    /* VARIABLES DE SOUS-TITRE */
    --subtitle-size: 1.1rem;                            /* 17.6px */
    --subtitle-weight: 500;                             /* Moyen */
  }

  /* ===============================================
   * STYLES DE BASE POUR TOUS LES ÉLÉMENTS
   * =============================================== */

  /**
   * RESET CSS: Suppression des marges/padding par défaut
   */
  * {
    margin: 0;                       /* Supprime toutes les marges */
    padding: 0;                      /* Supprime tous les paddings */
    box-sizing: border-box;          /* Border-box pour tous les éléments */
  }

  /**
   * STYLES HTML/BODY: Configuration de base de la page
   */
  html, body {
    font-family: var(--font-primary);          /* Police principale */
    font-size: 16px;                           /* Taille de base (rem reference) */
    line-height: 1.5;                          /* Interlignage pour lisibilité */
    color: var(--gray-dark);                   /* Couleur de texte par défaut */
    background-color: var(--light);            /* Arrière-plan gris très clair */
    min-height: 100vh;                         /* Hauteur minimale = viewport */
    scroll-behavior: smooth;                   /* Scroll fluide pour ancres */
    scroll-padding-top: 80px;                  /* Offset pour navbar fixe */
    -webkit-font-smoothing: antialiased;       /* Lissage police (WebKit) */
    -moz-osx-font-smoothing: grayscale;        /* Lissage police (Firefox) */
  }

  /**
   * CLASSE UTILITAIRE: Bloquer le scroll (pour modales)
   */
  body.scroll-locked {
    overflow: hidden;                          /* Empêche le scroll */
  }

  /**
   * STYLES TITRES: Hiérarchie typographique H1-H6
   */
  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-primary);          /* Police principale pour titres */
    font-weight: 600;                          /* Semi-gras par défaut */
    line-height: 1.2;                          /* Interlignage serré pour titres */
    margin-bottom: var(--space-md);            /* Marge inférieure standardisée */
    color: var(--dark);                        /* Couleur sombre pour contraste */
  }

  h1 { font-size: var(--font-size-xxxl); }    /* 48px */
  h2 { font-size: var(--font-size-xxl); }     /* 32px */
  h3 { font-size: var(--font-size-xl); }      /* 24px */
  h4 { font-size: var(--font-size-lg); }      /* 20px */
  h5 { font-size: var(--font-size-md); }      /* 16px */
  h6 { font-size: var(--font-size-sm); }      /* 14px */

  /**
   * STYLES PARAGRAPHES ET LISTES: Espacement standardisé
   */
  p, ul, ol {
    margin-bottom: var(--space-md);            /* Marge inférieure pour séparation */
  }

  /**
   * STYLES LIENS: Couleur et transitions
   */
  a {
    color: var(--primary);                     /* Couleur bleue par défaut */
    text-decoration: none;                     /* Pas de soulignement par défaut */
    transition: color var(--transition-normal); /* Animation de couleur */
  }

  a:hover {
    color: var(--primary-dark);                /* Bleu foncé au survol */
  }

  /**
   * STYLES BOUTONS: Apparence standardisée
   */
  button, .button {
    cursor: pointer;                           /* Curseur pointer */
    background: var(--primary);                /* Fond bleu */
    color: var(--white);                       /* Texte blanc */
    border: none;                              /* Pas de bordure */
    padding: var(--space-sm) var(--space-lg);  /* Padding standardisé */
    border-radius: var(--border-radius-md);    /* Coins arrondis */
    font-family: var(--font-primary);          /* Police cohérente */
    font-size: var(--font-size-md);            /* Taille standard */
    font-weight: 500;                          /* Poids moyen */
    transition: all var(--transition-normal);   /* Animation complète */
  }

  button:hover, .button:hover {
    background: var(--primary-dark);           /* Fond plus foncé au survol */
    transform: translateY(-2px);               /* Légère élévation */
    box-shadow: var(--shadow-md);              /* Ombre pour profondeur */
  }

  button:disabled, .button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  input, textarea {
    font-family: var(--font-primary);
    font-size: var(--font-size-md);
    padding: var(--space-sm) var(--space-md);
    border: 1px solid var(--gray-lighter);
    border-radius: var(--border-radius-md);
    width: 100%;
    transition: border-color var(--transition-normal);
  }

  input:focus, textarea:focus {
    outline: none;
    border-color: var(--primary);
  }

  /* Styles de base pour les select NON stylés avec styled-components
   * Les select stylés (ex: SortDropdownStyle) doivent définir leurs propres styles complets
   * pour éviter les conflits et garantir un rendu personnalisé cohérent.
   */
  select:not([class*="sc-"]) {
    font-family: var(--font-primary);
    font-size: var(--font-size-md);
    padding: var(--space-sm) var(--space-md);
    border: 1px solid var(--gray-lighter);
    border-radius: var(--border-radius-md);
    width: 100%;
    transition: border-color var(--transition-normal);
  }

  select:not([class*="sc-"]):focus {
    outline: none;
    border-color: var(--primary);
  }

  .container {
    width: 100%;
    max-width: var(--container-xl);
    margin: 0 auto;
    padding: 0 var(--space-lg);
  }

  section {
    padding: var(--space-xxl) 0;
  }

  .text-center {
    text-align: center;
  }

  .text-right {
    text-align: right;
  }

  .flex {
    display: flex;
  }

  .flex-col {
    flex-direction: column;
  }

  .items-center {
    align-items: center;
  }

  .justify-center {
    justify-content: center;
  }

  .justify-between {
    justify-content: space-between;
  }

  .flex-wrap {
    flex-wrap: wrap;
  }

  .grid {
    display: grid;
  }

  .grid-cols-2 {
    grid-template-columns: repeat(2, 1fr);
  }

  .grid-cols-3 {
    grid-template-columns: repeat(3, 1fr);
  }

  .grid-cols-4 {
    grid-template-columns: repeat(4, 1fr);
  }

  .gap-sm {
    gap: var(--space-sm);
  }

  .gap-md {
    gap: var(--space-md);
  }

  .gap-lg {
    gap: var(--space-lg);
  }

  .mt-sm { margin-top: var(--space-sm); }
  .mt-md { margin-top: var(--space-md); }
  .mt-lg { margin-top: var(--space-lg); }

  .mb-sm { margin-bottom: var(--space-sm); }
  .mb-md { margin-bottom: var(--space-md); }
  .mb-lg { margin-bottom: var(--space-lg); }

  /* Animation pour les éléments qui apparaissent au scroll */
  .reveal {
    opacity: 0;
    transform: translateY(30px);
    transition: opacity 0.8s ease, transform 0.8s ease;
  }

  .reveal.active {
    opacity: 1;
    transform: translateY(0);
  }

  /* Délais pour les animations en séquence */
  .delay-100 { transition-delay: 0.1s; }
  .delay-200 { transition-delay: 0.2s; }
  .delay-300 { transition-delay: 0.3s; }
  .delay-400 { transition-delay: 0.4s; }
  .delay-500 { transition-delay: 0.5s; }

  /* Responsive */
  @media (max-width: 1024px) {
    :root {
      --font-size-xxxl: 2.5rem;
      --font-size-xxl: 1.75rem;
    }

    .container {
      padding: 0 var(--space-md);
    }

    section {
      padding: var(--space-xl) 0;
    }
  }

  @media (max-width: 768px) {
    :root {
      --font-size-xxxl: 2rem;
      --font-size-xxl: 1.5rem;
      --font-size-xl: 1.25rem;
    }

    .grid-cols-3, .grid-cols-4 {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 640px) {
    :root {
      --font-size-xxxl: 1.75rem;
      --font-size-xxl: 1.5rem;
    }

    .grid-cols-2, .grid-cols-3, .grid-cols-4 {
      grid-template-columns: 1fr;
    }
  }

  .page-title-global {
    font-size: var(--page-title-size);
    font-weight: var(--page-title-weight);
    color: var(--page-title-color);
    text-align: center;
    margin-top: var(--page-title-margin-top);
    margin-bottom: var(--space-xl);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: var(--page-title-underline-width);
      height: var(--page-title-underline-height);
      background-color: var(--page-title-underline-color);
      border-radius: 2px;
    }
  }

  .section-title-global {
    font-size: var(--section-title-size);
    font-weight: var(--section-title-weight);
    color: var(--page-title-color);
    text-align: center;
    margin-top: var(--space-xxl);
    margin-bottom: var(--space-xl);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: var(--section-title-underline-width);
      height: var(--section-title-underline-height);
      background-color: var(--page-title-underline-color);
      border-radius: 2px;
    }
  }

  .subtitle-global {
    font-size: var(--subtitle-size);
    font-weight: var(--subtitle-weight);
    color: var(--gray-dark);
    text-align: center;
    margin-top: var(--space-xl);
    margin-bottom: var(--space-lg);
  }
`;

export default GlobalStyles; 